import { Button } from '@ordo/ui/button'
import { Checkbox } from '@ordo/ui/checkbox'
import { Popover, PopoverContent, PopoverTrigger } from '@ordo/ui/popover'
import { Skeleton } from '@ordo/ui/skeleton'
import { Switch } from '@ordo/ui/switch'
import { ChevronDown } from 'lucide-react'
import { useId } from 'react'

interface SchoolPrintRosterControlsProps {
  noun: string
  options: readonly string[]
  selected: ReadonlySet<string>
  onSelectionChange: (selected: ReadonlySet<string>) => void
  grouped: boolean
  onGroupedChange: (grouped: boolean) => void
  groupingDisabled: boolean
  pending?: boolean
  error?: boolean
}

export function SchoolPrintRosterControls({
  noun,
  options,
  selected,
  onSelectionChange,
  grouped,
  onGroupedChange,
  groupingDisabled,
  pending,
  error,
}: SchoolPrintRosterControlsProps) {
  const groupingId = useId()
  if (pending) {
    return (
      <span className="mt-3 flex flex-wrap items-center gap-3" aria-label={`Loading ${noun}s`}>
        <Skeleton className="h-9 w-36" />
        <Skeleton className="h-5 w-40" />
      </span>
    )
  }
  if (error) {
    return (
      <span className="mt-3 block text-xs text-destructive">Couldn’t load {noun}s. Reopen Print to try again.</span>
    )
  }
  const all = selected.size === options.length
  const label = all
    ? `All ${noun}s`
    : selected.size === 0
      ? `Select ${noun}s`
      : selected.size === 1
        ? [...selected][0]
        : `${selected.size} ${noun}s`

  return (
    <span className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-3">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={options.length === 0}
            className="min-w-0 max-w-full gap-2">
            <span className="truncate">{label}</span>
            <ChevronDown className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-56 p-1">
          <label className="flex cursor-pointer items-center gap-2.5 rounded px-3 py-2 text-sm hover:bg-muted">
            <Checkbox
              checked={all ? true : selected.size > 0 ? 'indeterminate' : false}
              onCheckedChange={() => onSelectionChange(all ? new Set() : new Set(options))}
            />
            All {noun}s
          </label>
          <div className="my-1 border-t" />
          <div className="max-h-60 overflow-y-auto">
            {options.map((option) => (
              <label
                key={option}
                className="flex cursor-pointer items-center gap-2.5 rounded px-3 py-2 text-sm hover:bg-muted">
                <Checkbox
                  checked={selected.has(option)}
                  onCheckedChange={(checked) => {
                    const next = new Set(selected)
                    if (checked) {
                      next.add(option)
                    } else {
                      next.delete(option)
                    }
                    onSelectionChange(next)
                  }}
                />
                {option}
              </label>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      <span className="inline-flex items-center gap-2 text-xs font-medium">
        <Switch
          id={groupingId}
          checked={grouped}
          onCheckedChange={onGroupedChange}
          disabled={groupingDisabled}
          aria-label={`Group per ${noun}`}
          type="button"
        />
        <label htmlFor={groupingId} className={groupingDisabled ? 'cursor-default' : 'cursor-pointer'}>
          Group per {noun}
        </label>
      </span>
      {selected.size === 0 && options.length > 0 ? (
        <span className="basis-full text-xs text-muted-foreground">Select at least one {noun} to print.</span>
      ) : null}
    </span>
  )
}
